import React, { useState, useEffect } from 'react';
import { Plus, Loader2, Tag, CheckCircle, XCircle, FileText } from 'lucide-react';
import { apiFetch } from '../../../utils/api';

const ExpensesManagement: React.FC = () => {
    const [expenses, setExpenses] = useState<any[]>([]);
    const [categories, setCategories] = useState<any[]>([]);
    const [projects, setProjects] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);

    const [newExpense, setNewExpense] = useState({
        project_id: '',
        amount: 0,
        category_id: '',
        description: '',
        receipt_url: ''
    });

    const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');

    const fetchData = async () => {
        setLoading(true);
        try {
            const [expensesRes, categoriesRes, projectsRes] = await Promise.all([
                apiFetch('/expenses'),
                apiFetch('/expenses/categories'),
                apiFetch('/projects')
            ]);

            if (expensesRes.ok) setExpenses(await expensesRes.json());
            if (categoriesRes.ok) setCategories(await categoriesRes.json());
            if (projectsRes.ok) setProjects(await projectsRes.json());
        } catch (err) { console.error(err); } finally { setLoading(false); }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const res = await apiFetch('/expenses', {
                method: 'POST',
                body: JSON.stringify({ ...newExpense, admin_id: adminUser.id })
            });
            if (res.ok) {
                setShowCreateModal(false);
                setNewExpense({
                    project_id: '',
                    amount: 0,
                    category_id: '',
                    description: '',
                    receipt_url: ''
                });
                fetchData();
            }
        } catch (err) { console.error(err); }
    };

    const updateStatus = async (id: string, status: string) => {
        try {
            const res = await apiFetch(`/expenses/${id}/status`, {
                method: 'PATCH',
                body: JSON.stringify({ status, approved_by: adminUser.id })
            });
            if (res.ok) fetchData();
        } catch (err) { console.error(err); }
    };

    if (loading) return <div className="flex justify-center p-12"><Loader2 className="animate-spin text-blue-500" size={40} /></div>;

    return (
        <div className="space-y-8">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800">Expense Management</h2>
                    <p className="text-gray-500">Track and approve project & operational expenses</p>
                </div>
                <button
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center gap-2 bg-emerald-600 text-white px-6 py-3 rounded-xl hover:bg-emerald-700 transition font-bold shadow-lg shadow-emerald-200"
                >
                    <Plus size={20} /> File Expense
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                    <div className="text-gray-400 text-xs font-bold uppercase mb-1">Pending Approval</div>
                    <div className="text-2xl font-bold text-amber-600">
                        ${expenses.filter(e => e.status === 'Pending').reduce((sum, e) => sum + parseFloat(e.amount), 0).toLocaleString()}
                    </div>
                </div>
                <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                    <div className="text-gray-400 text-xs font-bold uppercase mb-1">Approved this Month</div>
                    <div className="text-2xl font-bold text-emerald-600">
                        ${expenses.filter(e => e.status === 'Approved').reduce((sum, e) => sum + parseFloat(e.amount), 0).toLocaleString()}
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <table className="w-full text-left">
                    <thead className="bg-gray-50/50">
                        <tr>
                            <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Expense / Date</th>
                            <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Category / Project</th>
                            <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Amount</th>
                            <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Status</th>
                            <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Receipt</th>
                            <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {expenses.map((e, i) => (
                            <tr key={i} className="hover:bg-gray-50">
                                <td className="px-6 py-4">
                                    <div className="font-bold text-gray-800">{e.description}</div>
                                    <div className="text-xs text-gray-400">{new Date(e.created_at).toLocaleDateString()}</div>
                                </td>
                                <td className="px-6 py-4">
                                    <div className="flex items-center gap-1.5 text-sm font-bold text-gray-700">
                                        <Tag size={14} className="text-gray-400" />
                                        {categories.find(c => c.id === e.category_id)?.name}
                                    </div>
                                    <div className="text-xs text-gray-400">
                                        {projects.find(p => p.id === e.project_id)?.name || 'General'}
                                    </div>
                                </td>
                                <td className="px-6 py-4 font-bold text-gray-900">${parseFloat(e.amount).toLocaleString()}</td>
                                <td className="px-6 py-4">
                                    <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${e.status === 'Approved' ? 'bg-emerald-100 text-emerald-700' :
                                        e.status === 'Pending' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                                        }`}>
                                        {e.status}
                                    </span>
                                </td>
                                <td className="px-6 py-4">
                                    {e.receipt_url && (
                                        <a href={e.receipt_url} target="_blank" rel="noreferrer" className="text-blue-500 hover:text-blue-700 transition">
                                            <FileText size={18} />
                                        </a>
                                    )}
                                </td>
                                <td className="px-6 py-4 flex gap-4">
                                    {e.status === 'Pending' && (
                                        <>
                                            <button onClick={() => updateStatus(e.id, 'Approved')} className="text-emerald-600 hover:text-emerald-800"><CheckCircle size={18} /></button>
                                            <button onClick={() => updateStatus(e.id, 'Rejected')} className="text-red-600 hover:text-red-800"><XCircle size={18} /></button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {showCreateModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-3xl p-8 max-w-xl w-full">
                        <h3 className="text-2xl font-bold mb-6">File New Expense</h3>
                        <form onSubmit={handleCreate} className="space-y-4">
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">Project (Optional)</label>
                                <select className="w-full border p-3 rounded-xl" value={newExpense.project_id} onChange={e => setNewExpense({ ...newExpense, project_id: e.target.value })}>
                                    <option value="">General Overhead</option>
                                    {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Category *</label>
                                    <select className="w-full border p-3 rounded-xl" value={newExpense.category_id} onChange={e => setNewExpense({ ...newExpense, category_id: e.target.value })} required>
                                        <option value="">Select Category</option>
                                        {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Amount ($) *</label>
                                    <input type="number" step="0.01" className="w-full border p-3 rounded-xl" value={newExpense.amount} onChange={e => setNewExpense({ ...newExpense, amount: parseFloat(e.target.value) })} required />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">Description *</label>
                                <textarea className="w-full border p-3 rounded-xl" rows={3} value={newExpense.description} onChange={e => setNewExpense({ ...newExpense, description: e.target.value })} required placeholder="Details about the expense..." />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">Receipt URL / Image Link</label>
                                <input className="w-full border p-3 rounded-xl" value={newExpense.receipt_url} onChange={e => setNewExpense({ ...newExpense, receipt_url: e.target.value })} placeholder="https://..." />
                            </div>
                            <div className="flex justify-end gap-4 pt-4">
                                <button type="button" onClick={() => setShowCreateModal(false)} className="px-6 py-2 text-gray-500 font-bold">Cancel</button>
                                <button type="submit" className="px-8 py-2 bg-emerald-600 text-white font-bold rounded-xl hover:bg-emerald-700 shadow-md shadow-emerald-100">Submit Expense</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ExpensesManagement;
